import { jsx, jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { DataTable, useStableReference } from "@teselagen/ui";
import { getCutsiteType, getVirtualDigest } from "@teselagen/sequence-utils";
import CutsiteFilter from "../CutsiteFilter";
import Ladder from "./Ladder";
import {
  Tabs,
  Tab,
  Button,
  InputGroup,
  Intent,
  Checkbox
} from "@blueprintjs/core";
import withEditorInteractions from "../withEditorInteractions";
import { userDefinedHandlersAndOpts } from "../Editor/userDefinedHandlersAndOpts";
import { pick } from "lodash-es";
const MAX_DIGEST_CUTSITES = 50;
const MAX_PARTIAL_DIGEST_CUTSITES = 10;
const onSingleSelectRow = ({ onFragmentSelect }) => {
  onFragmentSelect();
};
export const DigestTool = (props) => {
  const [selectedTab, setSelectedTab] = useState("virtualDigest");
  const {
    editorName,
    dimensions = {},
    digestTool: { selectedFragment, computePartialDigest },
    onDigestSave,
    updateComputePartialDigest,
    boxHeight,
    digestLaneRightClicked,
    ladders,
    sequenceData,
    sequenceLength,
    selectionLayerUpdate: _selectionLayerUpdate,
    updateSelectedFragment
  } = props;
  const isCircular = sequenceData.circular;
  const cutsites = sequenceData.cutsites;
  const computePartialDigestDisabled = cutsites.length > MAX_PARTIAL_DIGEST_CUTSITES;
  const computeDigestDisabled = cutsites.length > MAX_DIGEST_CUTSITES;
  const selectionLayerUpdate = useStableReference(_selectionLayerUpdate);
  const lanes = useMemo(() => {
    const { fragments } = getVirtualDigest({
      cutsites,
      sequenceLength,
      isCircular,
      computePartialDigest,
      computePartialDigestDisabled,
      computeDigestDisabled
    });
    const _lanes = [
      fragments.map((f) => ({
        ...f,
        onFragmentSelect: () => {
          selectionLayerUpdate.current({
            start: f.start,
            end: f.end,
            name: f.name
          });
          updateSelectedFragment(f.Intentid);
        }
      }))
    ];
    return _lanes;
  }, [
    computeDigestDisabled,
    computePartialDigest,
    computePartialDigestDisabled,
    cutsites,
    isCircular,
    selectionLayerUpdate,
    sequenceLength,
    updateSelectedFragment
  ]);
  const digestInfoLanes = useMemo(
    () => lanes[0].map(({ id, cut1, cut2, start, end, size, ...rest }) => {
      return {
        ...rest,
        id,
        start,
        end,
        length: size,
        leftCutter: cut1.restrictionEnzyme.name,
        rightCutter: cut2.restrictionEnzyme.name,
        leftOverhang: getCutsiteType(cut1.restrictionEnzyme),
        rightOverhang: getCutsiteType(cut2.restrictionEnzyme)
      };
    }),
    [lanes]
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        height: typeof dimensions.height === "string" ? 100 : dimensions.height,
        overflowY: "auto",
        padding: 10
      },
      children: [
        onDigestSave && /* @__PURE__ */ jsxs("div", { style: { display: "flex", marginBottom: 10 }, children: [
          /* @__PURE__ */ jsx(InputGroup, { placeholder: "My Digest" }),
          /* @__PURE__ */ jsxs(
            Button,
            {
              intent: Intent.PRIMARY,
              onClick: () => {
                onDigestSave({});
              },
              style: { marginLeft: 5 },
              children: [
                " ",
                "Save"
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between" }, children: [
          /* @__PURE__ */ jsx(
            Checkbox,
            {
              onChange: () => {
                updateComputePartialDigest(!computePartialDigest);
              },
              checked: computePartialDigest,
              label: /* @__PURE__ */ jsxs("span", { children: [
                "Show Partial Digests",
                " ",
                computePartialDigestDisabled ? /* @__PURE__ */ jsxs("span", { style: { fontSize: 10 }, children: [
                  " ",
                  "-- Disabled (only supports ",
                  MAX_PARTIAL_DIGEST_CUTSITES,
                  " or fewer cut sites)",
                  " "
                ] }) : null
              ] }),
              disabled: computePartialDigestDisabled
            }
          ),
          /* @__PURE__ */ jsx(
            "a",
            {
              target: "_blank",
              rel: "noopener noreferrer",
              href: "http://docs.teselagen.com/en/articles/5489322-restriction-digest-in-the-vector-editor",
              children: "Read the docs"
            }
          )
        ] }),
        "Choose your enzymes:",
        /* @__PURE__ */ jsx(
          CutsiteFilter,
          {
            ...pick(props, userDefinedHandlersAndOpts),
            editorName
          }
        ),
        /* @__PURE__ */ jsx("br", {}),
        computeDigestDisabled && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              color: "red",
              marginBottom: "6px",
              fontSize: "15px"
            },
            children: `>${MAX_DIGEST_CUTSITES} cut sites detected. Filter out additional
            restriction enzymes to visualize digest results`
          }
        ),
        /* @__PURE__ */ jsxs(
          Tabs,
          {
            selectedTabId: selectedTab,
            onChange: (id) => {
              setSelectedTab(id);
            },
            children: [
              /* @__PURE__ */ jsx(
                Tab,
                {
                  title: "Virtual Digest",
                  id: "virtualDigest",
                  panel: /* @__PURE__ */ jsx(
                    Ladder,
                    {
                      boxHeight,
                      lanes,
                      digestLaneRightClicked,
                      selectedFragment,
                      ladders
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx(
                Tab,
                {
                  title: "Digest Info",
                  id: "table",
                  panel: /* @__PURE__ */ jsx(
                    DataTable,
                    {
                      noRouter: true,
                      isSimple: true,
                      maxHeight: 400,
                      withSearch: false,
                      onSingleRowSelect: onSingleSelectRow,
                      formName: "digestInfoTable",
                      entities: digestInfoLanes,
                      schema
                    }
                  )
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx("br", {})
      ]
    }
  );
};
const schema = {
  fields: [
    { width: 60, path: "start", displayName: "Start", type: "string" },
    { width: 60, path: "end", displayName: "End", type: "string" },
    { width: 70, path: "length", displayName: "Length", type: "string" },
    { path: "leftCutter", displayName: "Left Cutter", type: "string" },
    { path: "leftOverhang", displayName: "Left Overhang", type: "string" },
    { path: "rightCutter", displayName: "Right Cutter", type: "string" },
    { path: "rightOverhang", displayName: "Right Overhang", type: "string" }
  ]
};
export default withEditorInteractions(DigestTool);
